import { CommonContext } from '@repo-level/core-front';
import { getCountryFlagIcon } from 'helpers/cdn';
import type { FactoryCookieProps } from 'helpers/common';
import { factoryCookie } from 'helpers/common';
import { useContext, useEffect, useMemo, useRef } from 'react';

export function useDeepCompareMemo<T>(value: T) {
  const ref = useRef<T>(value);

  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function isEqual<T>(a: T, b: T) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface UseCookieProps extends FactoryCookieProps {
  active?: boolean | undefined;
}

export function useCookie(props: UseCookieProps) {
  useEffect(() => {
    if (props.active === false) return;

    factoryCookie(props);
  }, [props]);
}

export enum CookieType {
  String = 0,
  Object = 1,
}

export const pickProperties = <T>(
  object: T | undefined,
  objectProperties: (keyof T)[]
) => {
  if (object == null) return;

  return objectProperties.reduce(
    (acc, property) => {
      if (object[property] != null) {
        acc = { ...acc, [property]: object[property] };
      }
      return acc;
    },
    {} as Partial<T>
  );
};

export const useCountryOptions = () => {
  const { countries } = useContext(CommonContext);

  const options = useMemo(
    () =>
      countries.map(({ name, code }) => ({
        name,
        nameS: name,
        value: code,
        icon: getCountryFlagIcon(code.toLowerCase()),
      })),
    [countries]
  );

  return options;
};
